/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  PayloadPersonPatch,
  Response,
  PersonaStructure,
  getPersonResponse,
  PepsStructure,
  getPepsResponse,
  PayloadRequestAvalAditional,
  PostPersonResponse,
  PayloadPersonPepsPost,
  PatchPersonResponse,
  UserAvalesResponse,
  UserAvales,
  EconomicEvaluationStructure,
  EmploymentInfoStructure,
  EconomicEvaluationResponse,
  EmploymentInfoResponse,
} from "@interfaces/index";
import { baseApi } from "./baseApi";

const personasApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getPersons: build.query<
      PersonaStructure[],
      { loanApplicationId: string; personTypeId?: string; personTypeCode?: string[]; id?: string }
    >({
      query: ({ loanApplicationId, personTypeId, personTypeCode, id }) => {
        let url = `personas?size=100&q=loan_application_id:${loanApplicationId}`;
        if (id) url += `&q=id:${id}`;
        if (personTypeId) url += `&q=person_type_id:${personTypeId}`;
        if (personTypeCode?.length) {
          personTypeCode.forEach((item) => {
            url += `&q_person_type_code=${item}`;
          });
        }
        return {
          url,
        };
      },
      transformResponse: (response: getPersonResponse) => {
        return response.data.data;
      },
    }),
    getPersonById: build.query<PersonaStructure, string>({
      query: (personId) => `personas/${personId}`,
      transformResponse: (response: any) => {
        return response.data;
      },
    }),
    patchPerson: build.mutation<PatchPersonResponse, { personId: string; body: PayloadPersonPatch }>({
      query: ({ personId, body }) => ({
        url: `personas/${personId}`,
        method: "PATCH",
        body: body,
      }),
    }),
    postPerson: build.mutation<PostPersonResponse, { body: PayloadPersonPatch }>({
      query: ({ body }) => ({
        url: `personas`,
        method: "POST",
        body: body,
      }),
    }),
    getPersonPeps: build.query<PepsStructure[], { personId: string }>({
      query: ({ personId }) => `personas/${personId}/peps`,
      transformResponse: (response: getPepsResponse) => {
        return response?.data || [];
      },
    }),
    postPersonPeps: build.mutation<Response, { personId: string; body: PayloadPersonPepsPost }>({
      query: ({ personId, body }) => ({
        url: `personas/${personId}/peps`,
        method: "POST",
        body,
      }),
    }),
    postPersonAvalAditional: build.mutation<Response, { body: PayloadRequestAvalAditional }>({
      query: ({ body }) => ({
        url: `personas/aval_adicional`,
        method: "POST",
        body,
      }),
    }),
    getPersonAvales: build.query<UserAvales[], string>({
      query: (personId) => `usuarios_avales?q=person_id:${personId}`,
      transformResponse: (response: UserAvalesResponse) => {
        return response.data.data;
      },
    }),
    getPersonEconomicEvaluation: build.query<EconomicEvaluationStructure, string>({
      query: (personId) => `personas/${personId}/economic_evaluation`,
      transformResponse: (response: EconomicEvaluationResponse) => {
        return response.data[0];
      },
    }),
    postPersonEconomicEvaluation: build.mutation<Response, { personId: string; body: EconomicEvaluationStructure }>({
      query: ({ personId, body }) => ({
        url: `personas/${personId}/economic_evaluation`,
        method: "POST",
        body,
      }),
    }),
    patchPersonEconomicEvaluation: build.mutation<
      Response,
      { personId: string; idEconomic: string; body: EconomicEvaluationStructure }
    >({
      query: ({ personId, idEconomic, body }) => ({
        url: `personas/${personId}/economic_evaluation/${idEconomic}`,
        method: "PATCH",
        body,
      }),
    }),
    getPersonEmployment: build.query<EmploymentInfoStructure, string>({
      query: (personId) => `personas/${personId}/employment`,
      transformResponse: (response: EmploymentInfoResponse) => {
        return response.data[0];
      },
    }),
    postPersonEmployment: build.mutation<Response, { personId: string; body: EmploymentInfoStructure }>({
      query: ({ personId, body }) => ({
        url: `personas/${personId}/employment`,
        method: "POST",
        body,
      }),
    }),
    patchPersonEmployment: build.mutation<
      Response,
      { personId: string; idEmployment: string; body: EmploymentInfoStructure }
    >({
      query: ({ personId, idEmployment, body }) => ({
        url: `personas/${personId}/employment/${idEmployment}`,
        method: "PATCH",
        body,
      }),
    }),
  }),
});

export const {
  useLazyGetPersonsQuery,
  useLazyGetPersonByIdQuery,
  usePatchPersonMutation,
  usePostPersonMutation,
  useLazyGetPersonPepsQuery,
  usePostPersonPepsMutation,
  usePostPersonAvalAditionalMutation,
  useLazyGetPersonAvalesQuery,
  useLazyGetPersonEconomicEvaluationQuery,
  useLazyGetPersonEmploymentQuery,
  usePostPersonEconomicEvaluationMutation,
  usePatchPersonEconomicEvaluationMutation,
  usePostPersonEmploymentMutation,
  usePatchPersonEmploymentMutation,
} = personasApi;
